import { type ContextMenuItem, type LocalApi } from "@cmmd-forge/contracts";

import { resetGitStatusStateForTests } from "./lib/gitStatusState";
import { resetRequestLatencyStateForTests } from "./rpc/requestLatencyState";
import { applyEnvironmentUpdated, resetServerStateForTests } from "./rpc/serverState";
import { resetWsConnectionStateForTests } from "./rpc/wsConnectionState";
import { resetSavedEnvironmentRegistryStoreForTests } from "./environments/runtime/catalog";
import { resetSavedEnvironmentRuntimeStoreForTests } from "./environments/runtime/savedEnvironmentRuntimeStore";
import {
  getPrimaryEnvironmentConnection,
  resetEnvironmentServiceForTests,
} from "./environments/runtime/service";
import { type WsRpcClient } from "./rpc/wsRpcClient";
import { showContextMenuFallback } from "./contextMenuFallback";
import {
  getClientSettings,
  getSavedEnvironmentRegistry,
  getSavedEnvironmentSecret,
  removeSavedEnvironmentSecret,
  setClientSettings,
  setSavedEnvironmentRegistry,
  setSavedEnvironmentSecret,
} from "./clientPersistenceApi";

let cachedApi: LocalApi | undefined;

export function getCurrentPrimaryServerApi(): LocalApi["server"] {
  return createServerApi(getPrimaryEnvironmentConnection().client);
}

export function createServerApi(rpcClient: WsRpcClient): LocalApi["server"] {
  return {
    getConfig: rpcClient.server.getConfig,
    refreshProviders: rpcClient.server.refreshProviders,
    startProviderAuth: rpcClient.server.startProviderAuth,
    repairProviderAuthRuntime: rpcClient.server.repairProviderAuthRuntime,
    disconnectProviderAuth: rpcClient.server.disconnectProviderAuth,
    getProviderAuthSession: rpcClient.server.getProviderAuthSession,
    cancelProviderAuth: rpcClient.server.cancelProviderAuth,
    submitProviderAuthCode: rpcClient.server.submitProviderAuthCode,
    syncPersonalCodexToCloud: rpcClient.server.syncPersonalCodexToCloud,
    syncPersonalClaudeToCloud: rpcClient.server.syncPersonalClaudeToCloud,
    upsertKeybinding: rpcClient.server.upsertKeybinding,
    removeKeybinding: rpcClient.server.removeKeybinding,
    getSettings: rpcClient.server.getSettings,
    updateSettings: rpcClient.server.updateSettings,
    updateEnvironmentDisplayName: async (displayName) => {
      const result = await rpcClient.server.updateEnvironmentDisplayName({ displayName });
      applyEnvironmentUpdated(result.environment);
      return result;
    },
  };
}

function mergeLocalApi(baseApi: LocalApi, nativeApi: Partial<LocalApi>): LocalApi {
  return {
    ...baseApi,
    ...nativeApi,
    dialogs: {
      ...baseApi.dialogs,
      ...nativeApi.dialogs,
    },
    shell: {
      ...baseApi.shell,
      ...nativeApi.shell,
    },
    contextMenu: {
      ...baseApi.contextMenu,
      ...nativeApi.contextMenu,
    },
    persistence: {
      ...baseApi.persistence,
      ...nativeApi.persistence,
    },
    server: {
      ...baseApi.server,
      ...nativeApi.server,
    },
  };
}

export function createLocalApi(rpcClient: WsRpcClient): LocalApi {
  return {
    dialogs: {
      pickFolder: async () => {
        if (!window.desktopBridge) return null;
        return window.desktopBridge.pickFolder();
      },
      confirm: async (message) => {
        if (window.desktopBridge) {
          return window.desktopBridge.confirm(message);
        }
        return window.confirm(message);
      },
    },
    shell: {
      openInEditor: (cwd, editor) => rpcClient.shell.openInEditor({ cwd, editor }),
      openExternal: async (url) => {
        if (window.desktopBridge) {
          const opened = await window.desktopBridge.openExternal(url);
          if (!opened) {
            throw new Error("Unable to open link.");
          }
          return;
        }

        window.open(url, "_blank", "noopener,noreferrer");
      },
    },
    contextMenu: {
      show: async <T extends string>(
        items: readonly ContextMenuItem<T>[],
        position?: { x: number; y: number },
      ): Promise<T | null> => {
        if (window.desktopBridge) {
          return window.desktopBridge.showContextMenu(items, position) as Promise<T | null>;
        }
        return showContextMenuFallback(items, position);
      },
    },
    persistence: {
      getClientSettings,
      setClientSettings,
      getSavedEnvironmentRegistry,
      setSavedEnvironmentRegistry,
      getSavedEnvironmentSecret,
      setSavedEnvironmentSecret,
      removeSavedEnvironmentSecret,
    },
    server: createServerApi(rpcClient),
  };
}

export function readLocalApi(): LocalApi | undefined {
  if (typeof window === "undefined") return undefined;
  if (cachedApi) return cachedApi;

  const baseApi = createLocalApi(getPrimaryEnvironmentConnection().client);
  if (window.nativeApi) {
    cachedApi = mergeLocalApi(baseApi, window.nativeApi);
    return cachedApi;
  }

  cachedApi = baseApi;
  return cachedApi;
}

export function ensureLocalApi(): LocalApi {
  const api = readLocalApi();
  if (!api) {
    throw new Error("Local API not found");
  }
  return api;
}

export async function __resetLocalApiForTests() {
  cachedApi = undefined;
  const { __resetClientSettingsPersistenceForTests } = await import("./hooks/useSettings");
  __resetClientSettingsPersistenceForTests();
  await resetEnvironmentServiceForTests();
  resetGitStatusStateForTests();
  resetRequestLatencyStateForTests();
  resetSavedEnvironmentRegistryStoreForTests();
  resetSavedEnvironmentRuntimeStoreForTests();
  resetServerStateForTests();
  resetWsConnectionStateForTests();
}
